// src/RootApp.jsx
// Orchestrates: API key gate → Login/Signup → Main App
import { useState, useEffect } from "react";
import { QRCodeSVG as QRCode } from "qrcode.react";
import App, { setApiKey, setSessionToken, setUsageState } from "./App";
import LoginPage from "./LoginPage";
import { getSession, getToken, clearSession, validateSession, signOut } from "./auth";

const MOBILE_CSS = `
  @media(max-width:768px){
    /* Mobile: place user badge beside NEXUS (top-left) and smaller */
    .nx-fixed-overlay{
      position:fixed!important;
      top:10px!important;
      left:104px!important;
      right:auto!important;
      bottom:auto!important;
      transform:none!important;
      gap:6px!important;
    }
    /* Only show avatar circle — hide text labels to keep it small */
    .nx-upgrade-btn{font-size:.62em!important;padding:4px 8px!important}
    .nx-badge-name{display:none!important}
    .nx-badge-plan{display:none!important}
    .nx-badge-caret{display:none!important}
    /* Mobile dropdown: fixed under header, readable in light mode */
    .nx-badge-menu{
      position:fixed!important;
      top:58px!important;
      left:12px!important;
      right:12px!important;
      bottom:auto!important;
      transform:none!important;
      min-width:unset!important;
      max-width:unset!important;
    }
    /* Make the badge button + avatar smaller */
    .nx-fixed-overlay button{transform:scale(.92)!important;transform-origin:left center!important}
    .nx-fixed-overlay button > div{width:22px!important;height:22px!important}
    .nx-fixed-overlay button{padding:4px 8px 4px 5px!important}
    /* Modals full-width on mobile */
    .nx-pricing-modal{padding:18px 14px!important;border-radius:16px!important;margin:8px!important;max-width:calc(100vw - 16px)!important}
    .nx-pricing-grid{grid-template-columns:1fr!important}
    .nx-pay-grid{grid-template-columns:1fr!important}
    .nx-manual-modal{padding:14px!important;border-radius:16px!important;margin:8px!important;max-width:calc(100vw - 16px)!important}
    /* No extra padding needed when badge is near header */
    .nx-main-content{padding-bottom:14px!important}
  }
  @media(max-width:480px){
    .nx-fixed-overlay{top:9px!important;left:96px!important}
  }
`;

/* ── API Key Gate ─────────────────────────────────────────── */
function ApiKeyGate({ onDone }) {
  const [keyInput, setKeyInput] = useState("");
  const [error,    setError]    = useState("");

  function save() {
    const k = keyInput.trim();
    if (!k.startsWith("AIza")) {
      setError("Gemini key should start with AIza...");
      return;
    }
    setApiKey(k);
    localStorage.setItem("nx_key", k);
    onDone();
  }

  return (
    <div style={{minHeight:"100vh",background:"#07070f",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Syne','Segoe UI',sans-serif"}}>
      <style>{"@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Serif+Display:ital@0;1&display=swap')"}</style>
      <div style={{maxWidth:460,width:"90%",background:"rgba(255,255,255,.034)",border:"1px solid rgba(255,255,255,.08)",borderRadius:20,padding:"40px 36px",textAlign:"center",boxShadow:"0 40px 100px rgba(0,0,0,.5)"}}>
        <div style={{fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:"1.9em",color:"#fff",marginBottom:4}}>
          NE<span style={{color:"#ff3c5f"}}>X</span>US
        </div>
        <div style={{fontFamily:"'DM Serif Display',serif",fontStyle:"italic",fontSize:".9em",color:"rgba(255,255,255,.38)",marginBottom:28}}>
          Powered by Gemini 2.5 Pro
        </div>
        <div style={{textAlign:"left",marginBottom:16}}>
          <label style={{fontSize:".68em",color:"rgba(255,255,255,.3)",letterSpacing:"1.5px",display:"block",marginBottom:6}}>
            GOOGLE GEMINI API KEY
          </label>
          <input
            type="password"
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && save()}
            placeholder="AIzaSy..."
            style={{width:"100%",background:"rgba(255,255,255,.05)",border:`1px solid ${error?"rgba(255,60,95,.5)":"rgba(255,255,255,.1)"}`,borderRadius:10,color:"#e8e8f2",padding:"12px 14px",fontFamily:"'Syne',sans-serif",fontSize:".87em",outline:"none",boxSizing:"border-box"}}
          />
          {error && <div style={{fontSize:".74em",color:"#ff3c5f",marginTop:6}}>{error}</div>}
        </div>
        <button onClick={save} style={{width:"100%",background:"linear-gradient(135deg,#ff3c5f,#9b1dff)",border:"none",color:"#fff",padding:"13px",borderRadius:10,cursor:"pointer",fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:".88em",marginBottom:18}}>
          Launch NEXUS →
        </button>
        <div style={{fontSize:".7em",color:"rgba(255,255,255,.25)",lineHeight:1.7}}>
          Get your <strong style={{color:"rgba(255,255,255,.4)"}}>FREE</strong> key at{" "}
          <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" style={{color:"#ff3c5f",textDecoration:"none"}}>aistudio.google.com/apikey</a>
          <br/>No credit card · Generous free tier · 30 seconds to set up
        </div>
      </div>
    </div>
  );
}

/* ── User Badge ───────────────────────────────────────────── */
function UserBadge({ user, onLogout, onDeleteAccount }) {
  const [open, setOpen] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delPw, setDelPw] = useState(""); 
  const [delErr, setDelErr] = useState(""); 
  const [delLoading, setDelLoading] = useState(false);

  async function confirmDelete() {
    if(!delPw.trim()) { setDelErr("Enter your password to confirm"); return; }
    setDelLoading(true); setDelErr("");
    try {
      const res = await fetch("/api/auth/delete-account", {
        method:"POST",
        headers:{"Content-Type":"application/json","x-session-token":getToken()},
        body: JSON.stringify({password: delPw})
      });
      const d = await res.json();
      if(res.ok) { onLogout(); }
      else { setDelErr(d.error||"Failed to delete account"); }
    } catch(e) { setDelErr(e.message); }
    setDelLoading(false);
  }
  const isOwner = user.plan === "owner";
  const isPaid  = user.plan === "paid";

  const planLabel = isOwner ? "👑 OWNER" : isPaid ? "💎 PRO" : "🆓 FREE";
  const planColor = isOwner ? "#ffd166" : isPaid ? "#a8ff78" : "var(--txt2)";
  const avatarBg  = isOwner
    ? "linear-gradient(135deg,#ffd166,#ff8c42)"
    : isPaid
    ? "linear-gradient(135deg,#a8ff78,#00d4ff)"
    : "linear-gradient(135deg,#ff3c5f,#9b1dff)";

  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  const MANUAL_SECTIONS = [
    {
      icon:"◈", title:"AI Assistant",
      color:"#ff3c5f",
      lines:[
        "Chat with AI on any topic — coding, writing, analysis, Q&A",
        "Upload PDF or images and ask questions about them",
        "Use the 🎤 mic button to speak your message (Chrome/Edge)",
        "Click Copy on any response to copy the full text",
        isOwner||isPaid ? "Unlimited messages per month" : "10 messages/day on Free plan",
      ]
    },
    {
      icon:"</>", title:"Code Lab",
      color:"#00d4ff",
      lines:[
        "Generate code in any language from a description",
        "Upload existing code files to get them explained or fixed",
        "Click Run to simulate code execution in the browser",
        "Copy or download the generated code with one click",
        isOwner||isPaid ? "500 generations/month" : "5 generations/day on Free plan",
      ]
    },
    {
      icon:"⬡", title:"Web Builder",
      color:"#a8ff78",
      lines:[
        "Choose a site type (E-Commerce, Restaurant, Portfolio, SaaS etc.)",
        "Choose a visual style (Modern Dark, Luxury, Cyberpunk etc.)",
        "Write a detailed description for best results — mention brand, colors, products",
        "Use Refine to tweak the generated site with follow-up instructions",
        "Download the HTML file and open it directly in any browser",
        "Tip: Add PEXELS_API_KEY in .env for real product photos",
        isOwner||isPaid ? "50 builds/month" : "1 build/day on Free plan",
      ]
    },
    {
      icon:"◉", title:"Mentor",
      color:"#ffd166",
      lines:[
        "Tutor: Type a topic and chat with an AI tutor — it teaches with examples",
        "Upload PDF/DOCX/images to generate study materials automatically",
        "Quiz: Enter a topic and difficulty level to get MCQ questions",
        "Interview: Pick a role, level, and domain for a mock interview session",
        "Use 🎤 mic to speak your interview answers out loud",
        "Resume: Fill in your details and choose a template — AI optimizes it",
        isOwner||isPaid ? "Unlimited quiz & tutor sessions" : "10 quiz + 10 tutor messages/day",
      ]
    },
    {
      icon:"◎", title:"Analyst",
      color:"#9b1dff",
      lines:[
        "Drag & drop or click to upload any CSV, Excel, or JSON file",
        "AI auto-detects data type and cleans messy data",
        "Generates a full business analysis report with key insights",
        "Creates charts automatically — bar, pie, line based on your data",
        "Download the cleaned dataset as a new CSV file",
        isOwner||isPaid ? "60 analyses/month" : "2 analyses/day on Free plan",
      ]
    },
    ...(isOwner ? [{
      icon:"⚙", title:"Admin Panel",
      color:"#ff8c42",
      lines:[
        "View all registered users with their plan and join date",
        "Upgrade any user to Pro or downgrade back to Free",
        "Delete user accounts permanently (removes all data)",
        "All plan changes trigger automatic email notifications",
        "Refresh the list anytime with the ↺ Refresh button",
      ]
    }] : []),
    {
      icon:"💡", title:"Tips & Limits",
      color:"rgba(255,255,255,.5)",
      lines:[
        "All usage limits reset on the 1st of every month",
        "Toggle 🌙/☀️ in the header to switch dark/light mode",
        isPaid||isOwner ? "Pro/Owner limits are very high — use freely" : "Upgrade to Pro for 10,000+ messages and 50 web builds/month",
        "All data is stored locally — sessions persist between visits",
        "For best AI results: be specific and detailed in your prompts",
      ]
    },
  ];

  return (
    <div style={{position:"relative"}} onClick={e => e.stopPropagation()}>
      <button onClick={() => setOpen(o => !o)} style={{display:"flex",alignItems:"center",gap:8,background:"var(--card-bg,rgba(255,255,255,.05))",border:`1px solid ${isOwner?"rgba(255,209,102,.3)":isPaid?"rgba(168,255,120,.2)":"var(--bdr,rgba(255,255,255,.1))"}`,borderRadius:40,padding:"5px 12px 5px 6px",cursor:"pointer",fontFamily:"'Syne',sans-serif",color:"var(--txt,rgba(255,255,255,.7))",transition:"all .18s"}}>
        <div style={{width:26,height:26,borderRadius:"50%",background:avatarBg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:".78em",fontWeight:700,color:"#fff",boxShadow:isOwner?"0 0 8px rgba(255,209,102,.5)":isPaid?"0 0 8px rgba(168,255,120,.3)":"none"}}>
          {isOwner ? "👑" : (user.avatar || user.name[0].toUpperCase())}
        </div>
        <span className="nx-badge-name" style={{fontSize:".78em",fontWeight:600,maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:"var(--txt)"}}>{user.name}</span>
        <span className="nx-badge-plan" style={{fontSize:".62em",color:planColor,fontWeight:700,letterSpacing:".5px"}}>{planLabel}</span>
        <span className="nx-badge-caret" style={{fontSize:".6em",opacity:.5}}>▾</span>
      </button>

      {open && (
        <div className="nx-badge-menu" style={{position:"absolute",top:"calc(100% + 8px)",right:0,background:"var(--bg2)",backdropFilter:"blur(20px)",border:"1px solid var(--bdr)",borderRadius:12,padding:8,minWidth:200,zIndex:500,boxShadow:"0 20px 50px rgba(0,0,0,.6)"}}>
          <div style={{padding:"8px 12px",borderBottom:"1px solid var(--bdr)",marginBottom:4}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{fontSize:".82em",fontWeight:700,color:"var(--txt)"}}>{user.name}</div>
              {isOwner && <span style={{fontSize:".55em",background:"rgba(255,209,102,.15)",border:"1px solid rgba(255,209,102,.3)",borderRadius:6,padding:"1px 6px",color:"#ffd166",letterSpacing:"1px"}}>OWNER</span>}
              {isPaid  && <span style={{fontSize:".55em",background:"rgba(168,255,120,.1)",border:"1px solid rgba(168,255,120,.25)",borderRadius:6,padding:"1px 6px",color:"#a8ff78",letterSpacing:"1px"}}>PRO</span>}
            </div>
            <div style={{fontSize:".7em",color:"var(--txt2)",marginTop:1}}>{user.email}</div>
            <div style={{fontSize:".65em",marginTop:3,color:planColor,filter:"var(--txt)"}}>{isOwner?"✦ Unlimited access — Platform owner":isPaid?"✦ Pro Plan — All features unlocked":"✦ Free tier — Upgrade for full access"}</div>
          </div>
          {[
            {icon:"📖", label:"How to Use", action:()=>{ setOpen(false); setShowManual(true); }},
            {icon:"🗑", label:"Delete Account", action:()=>{ setOpen(false); setShowDelete(true); setDelPw(""); setDelErr(""); }, danger:true},
            {icon:"🚪", label:"Sign Out", action:()=>{ onLogout(); setOpen(false); }, danger:true},
          ].map(item => (
            <button key={item.label} onClick={item.action}
              style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"none",border:"none",cursor:"pointer",padding:"9px 12px",borderRadius:8,textAlign:"left",fontFamily:"'Syne',sans-serif",fontSize:".8em",fontWeight:600,color:item.danger?"#ff3c5f":"var(--txt)",transition:"all .15s"}}
              onMouseEnter={e=>e.currentTarget.style.background=item.danger?"rgba(255,60,95,.1)":"rgba(255,255,255,.06)"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}
            >
              <span>{item.icon}</span>{item.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Manual Modal ───────────────────────────────────── */}
      {showManual && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={()=>setShowManual(false)}>
          <div className="nx-manual-modal" style={{background:"var(--bg2)",border:"1px solid var(--bdr)",borderRadius:20,width:"100%",maxWidth:620,maxHeight:"88vh",overflowY:"auto",fontFamily:"'Syne',sans-serif",boxShadow:"0 40px 100px rgba(0,0,0,.8)"}}
            onClick={e=>e.stopPropagation()}>
            {/* Header */}
            <div style={{padding:"22px 24px 16px",borderBottom:"1px solid var(--bdr)",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:"var(--bg2)",zIndex:1,borderRadius:"20px 20px 0 0"}}>
              <div>
                <div style={{fontSize:"1.2em",fontWeight:800,color:"var(--txt)"}}>📖 NEXUS Manual</div>
                <div style={{fontSize:".7em",color:"var(--txt2)",marginTop:3,letterSpacing:".5px"}}>
                  {isOwner?"Owner Edition — All modules, no limits":isPaid?"Pro Edition — All features unlocked":"Free Edition — Upgrade anytime for more"}
                </div>
              </div>
              <button onClick={()=>setShowManual(false)} style={{background:"rgba(255,255,255,.07)",border:"1px solid var(--bdr)",color:"var(--txt)",borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:"1em",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>

            {/* Sections */}
            <div style={{padding:"16px 24px 24px",display:"flex",flexDirection:"column",gap:16}}>
              {MANUAL_SECTIONS.map(sec=>(
                <div key={sec.title} style={{background:"rgba(255,255,255,.03)",border:`1px solid rgba(255,255,255,.07)`,borderRadius:12,padding:"14px 16px",borderLeft:`3px solid ${sec.color}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <span style={{fontFamily:"monospace",fontSize:"1em",color:sec.color}}>{sec.icon}</span>
                    <span style={{fontWeight:700,fontSize:".88em",color:"var(--txt)"}}>{sec.title}</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {sec.lines.map((line,i)=>(
                      <div key={i} style={{display:"flex",gap:8,fontSize:".78em",color:"var(--txt2)",lineHeight:1.6}}>
                        <span style={{color:sec.color,flexShrink:0,marginTop:2}}>›</span>
                        <span>{line}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Plan badge at bottom */}
              <div style={{textAlign:"center",padding:"12px",background:`rgba(${isOwner?"255,209,102":isPaid?"168,255,120":"255,60,95"},.06)`,border:`1px solid rgba(${isOwner?"255,209,102":isPaid?"168,255,120":"255,60,95"},.2)`,borderRadius:10}}>
                <div style={{fontSize:".72em",color:planColor,fontWeight:700,letterSpacing:"1px"}}>
                  {isOwner?"👑 OWNER — Unlimited everything. You built this.":isPaid?"💎 PRO — All features active. Limits reset every 1st of month.":"🆓 FREE — 10 msgs · 5 code · 1 web · 2 data per day. Resets monthly."}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Account Modal ─────────────────────────── */}
      {showDelete && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
          onClick={()=>setShowDelete(false)}>
          <div style={{background:"#0d0d1e",border:"1px solid rgba(255,60,95,.3)",borderRadius:16,padding:"24px",maxWidth:380,width:"100%",fontFamily:"'Syne',sans-serif"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:"1.1em",fontWeight:800,color:"#ff3c5f",marginBottom:8}}>🗑 Delete Account</div>
            <div style={{fontSize:".78em",color:"var(--txt2)",marginBottom:16,lineHeight:1.6}}>
              This permanently deletes your account and all data. This <strong style={{color:"var(--txt)"}}>cannot be undone</strong>. Enter your password to confirm.
            </div>
            <input type="password" value={delPw} onChange={e=>setDelPw(e.target.value)}
              placeholder="Your password" autoFocus
              style={{width:"100%",background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,60,95,.3)",borderRadius:9,color:"var(--txt)",padding:"10px 13px",fontFamily:"'Syne',sans-serif",fontSize:".85em",outline:"none",marginBottom:10}}
              onKeyDown={e=>e.key==="Enter"&&confirmDelete()}/>
            {delErr && <div style={{fontSize:".75em",color:"#ff3c5f",marginBottom:10}}>{delErr}</div>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowDelete(false)}
                style={{flex:1,background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",color:"var(--txt2)",borderRadius:9,padding:"10px",fontFamily:"'Syne',sans-serif",fontSize:".8em",fontWeight:600,cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={confirmDelete} disabled={delLoading}
                style={{flex:1,background:"rgba(255,60,95,.15)",border:"1px solid rgba(255,60,95,.35)",color:"#ff3c5f",borderRadius:9,padding:"10px",fontFamily:"'Syne',sans-serif",fontSize:".8em",fontWeight:700,cursor:delLoading?"not-allowed":"pointer",opacity:delLoading?.6:1}}>
                {delLoading?"Deleting…":"Delete Forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Root ─────────────────────────────────────────────────── */
export default function RootApp() {
  const [stage, setStage] = useState("loading");
  const [user,  setUser]  = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [rzLoading, setRzLoading] = useState(false);
  const [rzMsg, setRzMsg] = useState("");
  const [showOnboard, setShowOnboard] = useState(false);
  const [verifyOtp, setVerifyOtp] = useState("");
  const [verifyMsg, setVerifyMsg] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);

  async function loadUsage(token, userObj) {
    if (!token) return;
    // Set owner limits immediately
    const u = userObj || user;
    if (u && u.email === "rajarshipaul20@gmail.com") {
      setUsageState({ usage:{}, plan:"owner", limits:{ chat:999999,code:999999,web:999999,pdf:999999,ppt:999999,refine:999999,quiz:999999,tutor:999999,interview:999999,resume:999999,data:999999 }});
    }
    try {
      const res = await fetch("/api/usage", { headers: { "x-session-token": token } });
      if (res.ok) { const d = await res.json(); setUsageState(d); }
    } catch {}
  }

  useEffect(() => {
    fetch("/health")
      .then(r => r.json())
      .then(async data => {
        if (data.hasKey) {
          // Try to restore session
          const token = getToken();
          if (token) {
            const user = await validateSession();
            if (user) {
              setUser(user);
              setSessionToken(token);
              await loadUsage(token);
              setStage("app");
              return;
            }
          }
          setStage("login");
        } else {
          const saved = localStorage.getItem("nx_key");
          if (saved && saved !== "YOUR_GEMINI_KEY_HERE") {
            setApiKey(saved);
            const token = getToken();
            if (token) {
              const user = await validateSession();
              if (user) {
                setUser(user);
                setSessionToken(token);
                await loadUsage(token);
                setStage("app");
                return;
              }
            }
            setStage("login");
          } else {
            setStage("apikey");
          }
        }
      })
      .catch(async () => {
        const saved = localStorage.getItem("nx_key");
        if (saved && saved !== "YOUR_GEMINI_KEY_HERE") {
          setApiKey(saved);
          const session = getSession();
          if (session) { setUser(session); setStage("app"); }
          else setStage("login");
        } else {
          setStage("apikey");
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAuth(u, needsVerification) {
    setUser(u);
    const token = getToken();
    setSessionToken(token);
    // Set owner limits immediately before DB call
    const plan = u.email === "rajarshipaul20@gmail.com" ? "owner" : u.plan;
    const PLAN_LIMITS_CLIENT = {
      free:  { chat:10,    code:5,   web:1,  pdf:2,   ppt:2,   refine:3,   quiz:10,    tutor:10,    interview:1,  resume:1,  data:2  },
      paid:  { chat:10000, code:500, web:50, pdf:100, ppt:100, refine:200, quiz:99999, tutor:99999, interview:20, resume:10, data:60 },
      owner: { chat:999999,code:999999,web:999999,pdf:999999,ppt:999999,refine:999999,quiz:999999,tutor:999999,interview:999999,resume:999999,data:999999 },
    };
    setUsageState({ usage:{}, plan, limits: PLAN_LIMITS_CLIENT[plan] || PLAN_LIMITS_CLIENT.free });
    await loadUsage(token);
    if (needsVerification) { setStage("verify"); return; }
    // Show onboarding for first-time users
    const seen = localStorage.getItem("nx_onboarded");
    if (!seen) setShowOnboard(true);
    setTimeout(() => setStage("app"), 5100);
  }

  async function handleVerifyEmail() {
    if(!verifyOtp.trim()||verifyLoading) return;
    setVerifyLoading(true); setVerifyMsg("");
    try{
      const res=await fetch("/api/auth/verify-email",{
        method:"POST",
        headers:{"Content-Type":"application/json","x-session-token":getToken()},
        body:JSON.stringify({otp:verifyOtp.trim()}),
      });
      const d=await res.json();
      if(res.ok){
        setVerifyMsg("✅ Email verified! Welcome to NEXUS 🚀");
        const seen=localStorage.getItem("nx_onboarded");
        if(!seen) setShowOnboard(true);
        setTimeout(()=>setStage("app"),5100);
      } else { setVerifyMsg("⚠ "+( d.error||"Invalid OTP")); }
    }catch{ setVerifyMsg("⚠ Network error"); }
    finally{ setVerifyLoading(false); }
  }

  async function handleResendOtp() {
    setVerifyMsg("Sending new OTP…");
    try{
      const res=await fetch("/api/auth/resend-otp",{method:"POST",headers:{"x-session-token":getToken()}});
      const d=await res.json();
      setVerifyMsg(res.ok?"✅ New OTP sent to your email":"⚠ "+d.error);
    }catch{ setVerifyMsg("⚠ Network error"); }
  }

  async function handleLogout() {
    await signOut();
    setSessionToken("");
    setUser(null);
    setStage("login");
  }



  if (stage === "loading") return (
    <div style={{minHeight:"100vh",background:"#07070f",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:"#ff3c5f",boxShadow:"0 0 20px #ff3c5f",animation:"p 1s ease-in-out infinite"}}/>
      <style>{"@keyframes p{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.8);opacity:.4}}"}</style>
    </div>
  );

  if (stage === "apikey") return <ApiKeyGate onDone={() => setStage("login")} />;
  if (stage === "login")  return <LoginPage onAuth={(u,nv)=>handleAuth(u,nv)} />;

  if (stage === "verify") return (
    <div style={{minHeight:"100vh",background:"#07070f",display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"'Syne',sans-serif"}}>
      <div style={{background:"#0d0d1e",border:"1px solid rgba(255,60,95,.25)",borderRadius:20,padding:"36px 32px",maxWidth:420,width:"100%",boxShadow:"0 40px 100px rgba(0,0,0,.7)",textAlign:"center"}}>
        <div style={{fontSize:"2.5em",marginBottom:12}}>📧</div>
        <div style={{fontSize:"1.3em",fontWeight:800,color:"#fff",marginBottom:8}}>Verify your email</div>
        <div style={{fontSize:".8em",color:"rgba(255,255,255,.4)",marginBottom:24,lineHeight:1.6}}>
          We sent a 6-digit code to <strong style={{color:"rgba(255,255,255,.7)"}}>{user?.email}</strong>.<br/>Enter it below to activate your account.
        </div>
        <input
          type="text" maxLength={6} value={verifyOtp}
          onChange={e=>setVerifyOtp(e.target.value.replace(/\D/g,""))}
          onKeyDown={e=>e.key==="Enter"&&handleVerifyEmail()}
          placeholder="000000"
          style={{width:"100%",background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,60,95,.3)",borderRadius:12,color:"#fff",padding:"14px",fontFamily:"'Syne',sans-serif",fontSize:"1.6em",fontWeight:800,textAlign:"center",letterSpacing:"8px",outline:"none",marginBottom:12}}
        />
        {verifyMsg&&<div style={{fontSize:".78em",color:verifyMsg.startsWith("✅")?"#a8ff78":"#ff3c5f",marginBottom:12}}>{verifyMsg}</div>}
        <button onClick={handleVerifyEmail} disabled={verifyLoading||verifyOtp.length!==6}
          style={{width:"100%",background:"linear-gradient(135deg,#ff3c5f,#9b1dff)",border:"none",color:"#fff",borderRadius:12,padding:"13px",fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:".9em",cursor:verifyLoading||verifyOtp.length!==6?"not-allowed":"pointer",opacity:verifyOtp.length!==6?.5:1,marginBottom:10}}>
          {verifyLoading?"Verifying…":"✓ Verify Email"}
        </button>
        <button onClick={handleResendOtp}
          style={{background:"none",border:"none",color:"rgba(255,60,95,.5)",fontFamily:"'Syne',sans-serif",fontSize:".78em",cursor:"pointer",textDecoration:"underline"}}>
          Didn't get the code? Resend
        </button>
        <div style={{marginTop:16,fontSize:".7em",color:"rgba(255,255,255,.2)"}}>
          <button onClick={handleLogout} style={{background:"none",border:"none",color:"rgba(255,255,255,.2)",fontFamily:"'Syne',sans-serif",fontSize:"1em",cursor:"pointer",textDecoration:"underline"}}>
            Back to login
          </button>
        </div>
      </div>
    </div>
  );

  const isOwner = user?.plan === "owner";
  const isPaid  = user?.plan === "paid";

  // ── Razorpay payment handler ─────────────────────────────
  async function handleRazorpay() {
    setRzLoading(true); setRzMsg("");
    try {
      // Load Razorpay SDK
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = () => rej(new Error("Could not load Razorpay SDK"));
          document.head.appendChild(s);
        });
      }
      // Create order on server
      const orderRes = await fetch("/api/razorpay/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-token": getToken() },
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderData.error || "Failed to create order");

      // Open Razorpay checkout
      await new Promise((resolve, reject) => {
        const rz = new window.Razorpay({
          key: orderData.keyId,
          amount: orderData.amount,
          currency: orderData.currency,
          name: "NEXUS",
          description: "NEXUS Pro — Monthly Plan",
          order_id: orderData.orderId,
          prefill: { email: user.email, name: user.name },
          theme: { color: "#ff3c5f" },
          modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
          handler: async (response) => {
            try {
              // Verify payment on server
              const verifyRes = await fetch("/api/razorpay/verify", {
                method: "POST",
                headers: { "Content-Type": "application/json", "x-session-token": getToken() },
                body: JSON.stringify(response),
              });
              const verifyData = await verifyRes.json();
              if (!verifyRes.ok) throw new Error(verifyData.error || "Verification failed");
              // Reload usage to reflect new plan
              await loadUsage(getToken());
              setUser(u => ({ ...u, plan: "paid" }));
              setShowPricing(false);
              setRzMsg("✅ Payment successful! Your account is now Pro.");
              resolve();
            } catch(e) { reject(e); }
          },
        });
        rz.open();
      });
    } catch(e) {
      if (e.message !== "Payment cancelled") setRzMsg(`⚠ ${e.message}`);
    }
    setRzLoading(false);
  }

  return (
    <div style={{position:"relative"}}>
      <style>{MOBILE_CSS}</style>
      <App />
      <div className="nx-fixed-overlay" style={{position:"fixed",top:8,right:16,zIndex:400,display:"flex",alignItems:"center",gap:8}}>
        {!isOwner && !isPaid && (
          <button className="nx-upgrade-btn" onClick={()=>setShowPricing(true)}
            style={{background:"linear-gradient(135deg,rgba(255,60,95,.2),rgba(155,29,255,.2))",border:"1px solid rgba(255,60,95,.35)",borderRadius:20,padding:"5px 13px",color:"#ff3c5f",fontFamily:"'Syne',sans-serif",fontSize:".72em",fontWeight:700,cursor:"pointer",letterSpacing:".5px",transition:"all .2s"}}
            onMouseEnter={e=>e.currentTarget.style.background="linear-gradient(135deg,rgba(255,60,95,.35),rgba(155,29,255,.35))"}
            onMouseLeave={e=>e.currentTarget.style.background="linear-gradient(135deg,rgba(255,60,95,.2),rgba(155,29,255,.2))"}>
            💎 Upgrade
          </button>
        )}
        <UserBadge user={user} onLogout={handleLogout} onUpgrade={()=>setShowPricing(true)}  />
      </div>

      {/* Success toast after payment */}
      {rzMsg&&!showPricing&&(
        <div style={{position:"fixed",bottom:24,right:24,background:rzMsg.startsWith("✅")?"rgba(0,200,100,.15)":"rgba(255,60,95,.15)",border:`1px solid ${rzMsg.startsWith("✅")?"rgba(0,200,100,.4)":"rgba(255,60,95,.4)"}`,borderRadius:12,padding:"12px 18px",color:rzMsg.startsWith("✅")?"#a8ff78":"#ff3c5f",fontFamily:"'Syne',sans-serif",fontSize:".82em",fontWeight:600,zIndex:9999,maxWidth:320,backdropFilter:"blur(10px)"}}
          onClick={()=>setRzMsg("")}>
          {rzMsg}
        </div>
      )}

      {/* Onboarding Modal — shown once on first login */}
      {showOnboard && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"#0d0d1e",border:"1px solid rgba(255,60,95,.25)",borderRadius:20,padding:"28px 24px",maxWidth:480,width:"100%",fontFamily:"'Syne',sans-serif",boxShadow:"0 40px 100px rgba(0,0,0,.8)",textAlign:"center"}}>
            <div style={{fontSize:"2em",marginBottom:8}}>👋</div>
            <div style={{fontSize:"1.3em",fontWeight:800,color:"var(--txt)",marginBottom:6}}>Welcome to NEXUS</div>
            <div style={{fontSize:".8em",color:"var(--txt2)",marginBottom:20,lineHeight:1.7}}>Your all-in-one AI platform is ready. Here's what you can do:</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20,textAlign:"left"}}>
              {[
                ["◈","AI Assistant","Chat, analyse, ask anything"],
                ["</>","Code Lab","Generate & run code instantly"],
                ["⬡","Web Builder","Build full websites with AI"],
                ["◉","Study AI","Tutor, quiz, mock interviews"],
                ["◎","Data Lab","Upload CSV → instant insights"],
                ["📖","Manual","Tap your avatar → How to Use"],
              ].map(([ic,title,desc])=>(
                <div key={title} style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.07)",borderRadius:10,padding:"10px 12px"}}>
                  <div style={{fontFamily:"monospace",color:"#ff3c5f",fontSize:".9em",marginBottom:3}}>{ic} <span style={{fontFamily:"'Syne',sans-serif",fontWeight:700,color:"var(--txt)",fontSize:"1em"}}>{title}</span></div>
                  <div style={{fontSize:".7em",color:"var(--txt2)"}}>{desc}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:".72em",color:"var(--txt2)",marginBottom:16,background:"rgba(255,209,102,.06)",border:"1px solid rgba(255,209,102,.15)",borderRadius:9,padding:"9px 12px"}}>
              💡 Free plan: 10 chats · 5 code · 1 web build · 2 data reports per day. Resets monthly.
            </div>
            <button onClick={()=>{ localStorage.setItem("nx_onboarded","1"); setShowOnboard(false); }}
              style={{background:"linear-gradient(135deg,#ff3c5f,#9b1dff)",border:"none",color:"#fff",borderRadius:12,padding:"13px 32px",fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:".9em",cursor:"pointer",width:"100%"}}>
              Let's Go 🚀
            </button>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricing && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.75)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
          onClick={()=>setShowPricing(false)}>
          <div className="nx-pricing-modal" style={{background:"#0d0d1e",border:"1px solid rgba(255,60,95,.25)",borderRadius:22,padding:"28px 32px",maxWidth:580,width:"100%",fontFamily:"'Syne',sans-serif",boxShadow:"0 40px 100px rgba(0,0,0,.7)",maxHeight:"90vh",overflowY:"auto"}}
            onClick={e=>e.stopPropagation()}>

            {/* Header */}
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{fontSize:".65em",letterSpacing:"3px",color:"rgba(255,60,95,.6)",marginBottom:6}}>NEXUS PRO</div>
              <div style={{fontSize:"2.6em",fontWeight:800,color:"#fff",lineHeight:1}}>₹830 <span style={{fontSize:".38em",color:"rgba(255,255,255,.3)",fontWeight:400}}>/month</span></div>
              <div style={{fontSize:".75em",color:"rgba(255,255,255,.3)",marginTop:6}}>Per user · Resets monthly · Cancel anytime</div>
            </div>

            {/* Feature grid */}
            <div className="nx-pricing-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:18}}>
              {[
                ["◈ Assistant Chat","10,000 messages"],
                ["📄 PDF Export","100 documents"],
                ["🎞 Presentations","100 files"],
                ["⬡ Web Builder","50 sites"],
                ["🎤 Mock Interviews","20 sessions"],
                ["📋 Resume / CV","10 resumes"],
                ["◎ Data Lab","60 reports"],
                ["</> Code Lab","500 generations"],
              ].map(([feat,lim])=>(
                <div key={feat} style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.07)",borderRadius:10,padding:"9px 12px"}}>
                  <div style={{fontSize:".73em",fontWeight:700,color:"rgba(255,255,255,.65)",marginBottom:2}}>{feat}</div>
                  <div style={{fontSize:".68em",color:"#ff3c5f",fontWeight:700}}>{lim}</div>
                </div>
              ))}
            </div>

            {/* Payment options — Razorpay + QR side by side */}
            <div className="nx-pay-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>

              {/* Razorpay card */}
              <div style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,60,95,.2)",borderRadius:14,padding:"16px",textAlign:"center",display:"flex",flexDirection:"column",gap:10,justifyContent:"center"}}>
                <div style={{fontSize:".65em",letterSpacing:"2px",color:"rgba(255,255,255,.3)"}}>PAY ONLINE</div>
                <div style={{fontSize:".72em",color:"rgba(255,255,255,.4)",lineHeight:1.5}}>Cards · UPI · Netbanking<br/>Wallets — instant upgrade</div>
                <button onClick={handleRazorpay} disabled={rzLoading}
                  style={{background:"linear-gradient(135deg,#ff3c5f,#9b1dff)",border:"none",color:"#fff",borderRadius:10,padding:"11px",fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:".82em",cursor:rzLoading?"not-allowed":"pointer",opacity:rzLoading?.7:1,transition:"all .2s"}}>
                  {rzLoading?"Processing…":"💳 Pay ₹830"}
                </button>
                {rzMsg&&<div style={{fontSize:".7em",color:rzMsg.startsWith("✅")?"#a8ff78":"#ff3c5f"}}>{rzMsg}</div>}
              </div>

              {/* QR card */}
              <div style={{background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.08)",borderRadius:14,padding:"16px",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                <div style={{fontSize:".65em",letterSpacing:"2px",color:"rgba(255,255,255,.3)"}}>UPI / QR</div>
                <div style={{background:"#fff",padding:"8px",borderRadius:8,display:"inline-block"}}>
                  <QRCode value="upi://pay?pa=rajarshipaul20@okicici&pn=RajarshiPaul&am=830&cu=INR&tn=NexusPro" size={100}/>
                </div>
                <div style={{fontSize:".65em",color:"rgba(255,255,255,.3)",lineHeight:1.6}}>
                  Scan → Pay ₹830
                </div>
              </div>
            </div>

            {/* Free tier note */}
            <div style={{background:"rgba(255,255,255,.02)",border:"1px solid rgba(255,255,255,.05)",borderRadius:10,padding:"9px 12px",fontSize:".7em",color:"rgba(255,255,255,.3)",marginBottom:14,textAlign:"center"}}>
              🆓 Free tier · Chat 10 · Web 1 · PDF 2 · PPT 2 · Interview 1 · Data 2 · CV 1
            </div>

            <button onClick={()=>setShowPricing(false)}
              style={{width:"100%",background:"transparent",border:"none",color:"rgba(255,255,255,.2)",padding:"8px",cursor:"pointer",fontSize:".72em",fontFamily:"'Syne',sans-serif"}}>
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}